import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { ColumnType } from 'antd/lib/table/interface';
import {
  IDropdownOption,
  IUsersFilters,
  Nullable,
  PaginatedResponse,
  SessionStorageKey,
  UserDefault,
} from '@common/interfaces';
import { useRepository } from '@context';
import { Button, Main, Page, SearchField, Table, TableFilter, TablePagination, Tip } from '@components';
import { EyeIcon, FilterIcon, PlusIcon } from '@assets/svg';

import css from './styles.module.scss';
import { UserModal } from './components';

const DEFAULT_PAGE_SIZE = 10;

const AdminAreaUsersPage: FC = () => {
  const { accountRepository } = useRepository();

  const params = JSON.parse(sessionStorage.getItem(SessionStorageKey.USERS_LIST_PARAMS) || '{}') || {};

  const [page, setPage] = useState(params.page || 1);
  const [pageSize, setPageSize] = useState(params.pageSize || DEFAULT_PAGE_SIZE);
  const [order, setOrder] = useState(params.order || '');
  const [search, setSearch] = useState('');
  const [organisationFilter, setOrganisationFilter] = useState<IDropdownOption[]>(params.organisationFilter || []);
  const [facilityFilter, setFacilityFilter] = useState<IDropdownOption[]>(params.facilityFilter || []);
  const [areaFilter, setAreaFilter] = useState<IDropdownOption[]>(params.areaFilter || []);

  const [showUserModal, setShowUserModal] = useState(false);
  const [userData, setUserData] = useState<Nullable<UserDefault>>(null);

  const { data: users, isLoading: isDataLoading } = useQuery<PaginatedResponse<UserDefault>>(
    ['users', order, page, pageSize, search, organisationFilter, facilityFilter, areaFilter],
    () =>
      accountRepository.getUsers({
        limit: pageSize,
        offset: (page - 1) * 10,
        ordering: order,
        search,
        organisation: organisationFilter.map(({ value }) => value).join(','),
        facilities: facilityFilter.map(({ value }) => value).join(','),
        customer_area_location: areaFilter.map(({ value }) => value).join(','),
      })
  );

  const { data: filtersData, isLoading: isFiltersLoading } = useQuery<IUsersFilters>('users-filters', () =>
    accountRepository.getFilters()
  );

  const clearPagination = () => {
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setOrder('');
  };

  const clearFilters = () => {
    setOrganisationFilter([]);
    setFacilityFilter([]);
    setAreaFilter([]);
  };

  const clearAllParams = () => {
    clearPagination();
    clearFilters();
  };

  const handleSearch = (key: string) => {
    clearAllParams();
    setSearch(key);
  };

  useEffect(() => {
    sessionStorage.setItem(
      SessionStorageKey.USERS_LIST_PARAMS,
      JSON.stringify({
        order,
        page,
        pageSize,
      })
    );
  }, [order, page, pageSize]);

  const columns: ColumnType<UserDefault>[] = [
    {
      dataIndex: 'first_name',
      key: 'first_name',
      title: 'First name',
      sorter: true,
    },
    {
      dataIndex: 'last_name',
      key: 'last_name',
      title: 'Last name',
      sorter: true,
    },
    {
      dataIndex: 'email',
      key: 'email',
      title: 'Email',
      sorter: true,
    },
    {
      dataIndex: 'organisation',
      key: 'organisation',
      title: 'Organisation',
      sorter: true,
      filterIcon: () => (
        <FilterIcon className={`${css.filterIcon} ${organisationFilter.length > 0 ? css.active : ''}`} />
      ),
      filterDropdown: ({ confirm }) => (
        <TableFilter
          items={filtersData?.organisations}
          selectedItems={organisationFilter}
          onChange={(options) => {
            clearPagination();
            setOrganisationFilter(options);
            confirm();
          }}
        />
      ),
    },
    {
      dataIndex: 'facilities',
      key: 'facilities',
      title: 'Digital office',
      render: (_, { facilities }) =>
        facilities.length > 1 ? (
          <Tip className={css.note} text={facilities.map(({ city_name }) => city_name).join(', ')}>
            <EyeIcon />
            Multiple
          </Tip>
        ) : facilities.length > 0 ? (
          facilities[0]?.city_name
        ) : (
          '-'
        ),
      sorter: true,
      filterIcon: () => <FilterIcon className={`${css.filterIcon} ${facilityFilter.length > 0 ? css.active : ''}`} />,
      filterDropdown: ({ confirm }) => (
        <TableFilter
          items={filtersData?.facilities}
          selectedItems={facilityFilter}
          onChange={(options) => {
            clearPagination();
            setFacilityFilter(options);
            confirm();
          }}
        />
      ),
    },
    {
      dataIndex: 'customer_area_location',
      key: 'customer_area_location',
      title: 'Customer area',
      render: (_, { customer_area_location }) => customer_area_location?.city_name || '-',
      sorter: true,
      filterIcon: () => <FilterIcon className={`${css.filterIcon} ${areaFilter.length > 0 ? css.active : ''}`} />,
      filterDropdown: ({ confirm }) => (
        <TableFilter
          items={filtersData?.customer_areas}
          selectedItems={areaFilter}
          onChange={(options) => {
            clearPagination();
            setAreaFilter(options);
            confirm();
          }}
        />
      ),
    },
    // TO DO: Need BE
    // {
    //   dataIndex: 'admin_permission',
    //   key: 'admin_permission',
    //   title: 'Admin permission',
    //   render: (_, item) => '-',
    //   sorter: true,
    // },
  ];

  const loading = isDataLoading || isFiltersLoading;

  const results = users?.results || [];

  const isFiltered = organisationFilter.length || facilityFilter.length || areaFilter.length;

  return (
    <Main background='admin'>
      <Page>
        <div className={css.top}>
          <div className={css.title}>User Management</div>
          {results.length > 0 ? (
            <Button
              text='Create new user'
              variant='supernova'
              iconR={<PlusIcon />}
              onClick={() => setShowUserModal(true)}
              disabled
            />
          ) : null}
        </div>
        <section>
          <div className={css.topPanel}>
            <SearchField className={css.search} onChange={handleSearch} value={search} placeholder='Search user...' />
            {isFiltered ? <Button text='Reset filters' variant='text' onClick={clearAllParams} /> : null}
          </div>
          <Table
            className={css.table}
            columns={columns}
            data={results}
            loading={loading}
            onChangeColumnOrder={setOrder}
            variant='dark'
            tableLayout='fixed'
            // onRowClick={(user: UserDefault) => {
            //   setShowUserModal(true);
            //   setUserData(user);
            // }}
          />
          <TablePagination
            page={page}
            onChangePage={setPage}
            totalCount={users?.count}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            variant='dark'
          />
        </section>
      </Page>
      {showUserModal && (
        <UserModal
          userData={userData}
          closeModal={() => {
            setShowUserModal(false);
            setUserData(null);
          }}
        />
      )}
    </Main>
  );
};

export default AdminAreaUsersPage;
